import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  GridItem,
  Button,
  Heading,
  Text,
  Input,
  Select,
  VStack,
  HStack,
  Textarea,
  Badge,
  Progress,
  Icon,
  useColorModeValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
  Checkbox,
} from '@chakra-ui/react';
import {
  Play,
  Square,
  Trash2,
  Flag,
  ChevronDown,
  ChevronRight,
  Edit2,
  AlertTriangle,
} from 'lucide-react';
import { collection, query, where, getDocs, addDoc, Timestamp, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';
import { useL10Notes } from './hooks/useL10Notes';
import { useMetrics } from '../Scorecard/useMetrics';
import { useRocks } from '../Rocks/useRocks';
import { useMeetingIssues } from './hooks/useMeetingIssues';
import { useDepartments } from '../Departments/useDepartments';
import { useActiveMeeting } from './hooks/useActiveMeeting';
import { useRockDiscussions } from './hooks/useRockDiscussions';
import { db } from '../../../utils/init-firebase';
import { COLLECTION_NAMES, Issue, TangentAlert } from '../types';
import { SectionNotesEditor } from './components/SectionNotesEditor';
import { ConfirmModal } from './components/ConfirmModal';
import { OffTrackMetricsModal } from './components/OffTrackMetricsModal';
import { MeetingIssuesList } from './components/MeetingIssuesList';
import { RockReviewCard } from './components/RockReviewCard';
import { OutstandingIssuesModal } from './components/OutstandingIssuesModal';
import { ResolveIssueModal } from '../Rocks/components/ResolveIssueModal';
import { ScorecardWeeklyGrid } from '../Scorecard/components/ScorecardWeeklyGrid';
import { AddIssueModal } from '../Issues/components/AddIssueModal';
import { L10ActionItemSection } from '../ActionItems/components/L10ActionItemSection';
import { RockDiscussionPanel } from './components/RockDiscussionPanel';
import { EditRockCountersModal } from './components/EditRockCountersModal';
import { RecentWinsExcited } from './components/RecentWinsExcited';
import { TangentModal } from './components/TangentModal';
import { getOpenIssuesForRock, getComputedRockStatus } from '../Rocks/rocks.util';

interface MeetingSection {
  key: string;
  label: string;
  duration: number;
}

const sections: MeetingSection[] = [
  { key: 'segue', label: 'Segue', duration: 5 },
  { key: 'scorecard', label: 'Scorecard Review', duration: 5 },
  { key: 'rock_review', label: 'Rock Review', duration: 5 },
  { key: 'customer_employee_headlines', label: 'Customer/Employee Headlines', duration: 5 },
  { key: 'action_item_list', label: 'Action Items', duration: 5 },
  { key: 'ids', label: 'IDS (Identify, Discuss, Solve)', duration: 60 },
  { key: 'conclude', label: 'Conclude', duration: 5 },
];

export function L10MeetingPage() {
  const { currentUser, eosUsers: users } = useAuth();
  const usersLoading = !users;
  const {
    activeMeeting,
    sectionElapsedSeconds,
    totalElapsedSeconds,
    isPresenter,
    canPresent,
    loading: meetingLoading,
    startMeeting: startActiveMeeting,
    stopMeeting: stopActiveMeeting,
    updateSection,
    updateFilters,
    updateRockTypeFilter,
  } = useActiveMeeting();

  const meetingStarted = !!activeMeeting;
  const currentSectionIndex = activeMeeting?.currentSectionIndex || 0;
  const selectedUserId = activeMeeting?.selectedUserId || 'all';
  const rockTypeFilter = activeMeeting?.rockTypeFilter || 'all';

  const [selectedMeetingTimestamp, setSelectedMeetingTimestamp] = useState<string | null>(() => {
    return sessionStorage.getItem('l10_meeting_timestamp') || null;
  });
  // Convert meetingTimestamp to ISO string, handling Date, string, or Firestore Timestamp
  const getMeetingTimestampString = (timestamp: any): string | null => {
    if (!timestamp) return null;
    if (timestamp instanceof Date) return timestamp.toISOString();
    if (typeof timestamp === 'string') return timestamp;
    if (timestamp instanceof Timestamp) return timestamp.toDate().toISOString();
    if (timestamp?.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toISOString();
    }
    if (timestamp?.toMillis && typeof timestamp.toMillis === 'function') {
      return new Date(timestamp.toMillis()).toISOString();
    }
    return null;
  };

  const meetingTimestamp = meetingStarted
    ? getMeetingTimestampString(activeMeeting?.meetingTimestamp)
    : selectedMeetingTimestamp;

  const [meetingDate, setMeetingDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [sectionNotes, setSectionNotes] = useState<Record<string, string>>(() => {
    const storedTimestamp = sessionStorage.getItem('l10_meeting_timestamp');
    if (storedTimestamp) {
      const stored = sessionStorage.getItem('l10_draft_section_notes');
      return stored ? JSON.parse(stored) : {};
    }
    return {};
  });
  const [presenterNotes, setPresenterNotes] = useState<string>(() => {
    const storedTimestamp = sessionStorage.getItem('l10_meeting_timestamp');
    if (storedTimestamp) {
      return sessionStorage.getItem('l10_draft_presenter_notes') || '';
    }
    return '';
  });
  const [meetingSaved, setMeetingSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sectionNote, setSectionNote] = useState('');
  const [sectionNoteCategory, setSectionNoteCategory] = useState<'general' | 'department_communication'>('general');
  const [sectionNoteOwners, setSectionNoteOwners] = useState<Set<string>>(new Set());
  const [sectionNoteDepartments, setSectionNoteDepartments] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTimestamp, setDeleteTimestamp] = useState<string | null>(null);
  const [showOffTrackModal, setShowOffTrackModal] = useState(false);
  const [showTangentModal, setShowTangentModal] = useState(false);
  const [tangentTriggeredByUserId, setTangentTriggeredByUserId] = useState<string | null>(null);
  const [dismissedTangentAlerts, setDismissedTangentAlerts] = useState<Set<string>>(() => {
    // Load dismissed alerts from sessionStorage on mount
    const stored = sessionStorage.getItem('dismissed_tangent_alerts');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });
  const [showOutstandingIssuesModal, setShowOutstandingIssuesModal] = useState(false);
  const [outstandingIssuesCount, setOutstandingIssuesCount] = useState(0);
  const [rockForIssueRequired, setRockForIssueRequired] = useState<{ id: string; name: string } | null>(null);
  const [showAddIssueModal, setShowAddIssueModal] = useState(false);
  const [addIssueModalButtonText, setAddIssueModalButtonText] = useState<string>('Save Issue');
  const [addIssueModalContext, setAddIssueModalContext] = useState<'moving-to-off-track' | 'adding-to-on-track' | undefined>(undefined);
  const [pendingRockStatusChange, setPendingRockStatusChange] = useState<{
    rockId: string;
    newStatus: 'on-track' | 'off-track' | 'at-risk';
  } | null>(null);
  const [rockRefreshTrigger, setRockRefreshTrigger] = useState<Record<string, number>>({});
  const [allIssues, setAllIssues] = useState<Issue[]>([]);
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [resolvingIssue, setResolvingIssue] = useState<{
    issueId: string;
    issueName: string;
    relatedRockId?: string;
    isLastIssue: boolean;
  } | null>(null);
  const [expandedRocks, setExpandedRocks] = useState<Set<string>>(new Set());
  const [rockDetails, setRockDetails] = useState<Record<string, {
    milestones: any[];
    actionItems: any[];
    notes: any[];
    loading: boolean;
  }>>({});
  const [showEditCountersModal, setShowEditCountersModal] = useState(false);
  const [editingRockId, setEditingRockId] = useState<string | null>(null);
  const [editingRockName, setEditingRockName] = useState<string>('');
  const [editingConsecutive, setEditingConsecutive] = useState<number>(0);
  const [editingTotal, setEditingTotal] = useState<number>(0);
  const seguePopulatedRef = useRef(false);

  const [isActionItemAccordionOpen, setIsActionItemAccordionOpen] = useState(false);
  const [showAddActionItemNoteModal, setShowAddActionItemNoteModal] = useState(false);
  const [actionItemForNote, setActionItemForNote] = useState<any>(null);
  const [actionItemNoteText, setActionItemNoteText] = useState('');
  const presenterNotesRef = useRef<HTMLTextAreaElement>(null);
  const lastSyncedPresenterNotesRef = useRef<string>('');
  const isLoadingInitialNotes = useRef(false);
  const [showStopMeetingModal, setShowStopMeetingModal] = useState(false);

  const [lastStatusChange, setLastStatusChange] = useState<{
    rockId: string;
    previousStatus: string;
    newStatus: string;
    timestamp: number;
  } | null>(null);

  const {
    notes,
    structuredNotes,
    savedMeetings,
    loadingMeetings,
    saveNotes,
    addIndividualNote,
    deleteIndividualNote,
    deleteMeeting,
    loadSavedMeetings,
    saveOffTrackMetrics,
  } = useL10Notes(meetingTimestamp);

  const { 
    metrics, 
    metricGoals, 
    updateMetricGoal, 
    addMetricGoal 
  } = useMetrics(currentUser?.uid);
  const {
    rocks,
    updateRock,
    loadMilestonesForRock,
    loadIssuesForRock,
    loadActionItemsForRock,
    loadPreviousNotes,
    toggleMilestone,
  } = useRocks(currentUser?.uid);
  const { departments, loading: departmentsLoading } = useDepartments();

  const {
    meetingIssues,
    loading: issuesLoading,
    duplicates,
    autoAddOpenIssues,
    createAndAddIssue,
    resolveIssue,
    actuallyResolveIssue,
    clearDuplicates,
    refreshMeetingIssues,
  } = useMeetingIssues(meetingTimestamp);

  const {
    toggleFlagged,
    markAsReviewed,
    isFlagged,
    isReviewed,
    getFlaggedCount,
  } = useRockDiscussions(meetingTimestamp || undefined);

  const toast = useToast();

  // Auto-populate Segue notes
  useEffect(() => {
    const populateSegue = async () => {
      // Wait for saved meetings to load so we can accurately determine the last meeting date
      if (loadingMeetings) return;

      if (
        meetingStarted &&
        activeMeeting &&
        currentSectionIndex === 0 && // Segue is usually the first section
        !seguePopulatedRef.current &&
        isPresenter
      ) {
        // Check if we already have notes in segue section (locally or in props)
        const hasNotes = structuredNotes.individualNotes.segue && structuredNotes.individualNotes.segue.length > 0;
        
        if (!hasNotes) {
          seguePopulatedRef.current = true; // Mark as attempted to avoid loops
          try {
            // Fetch wins/excited from the last meeting or default to 7 days ago
            let filterDate: Timestamp;
            
            // Find the most recent previous meeting
            const currentMeetingTime = activeMeeting.meetingTimestamp.toDate().getTime();
            const previousMeeting = savedMeetings.find(m => {
               const t = new Date(m.meeting_timestamp).getTime();
               // Ensure strictly less than current meeting to assume it's a previous one
               return t < currentMeetingTime - 1000; 
            });

            if (previousMeeting) {
              filterDate = Timestamp.fromDate(new Date(previousMeeting.meeting_timestamp));
            } else {
              const sevenDaysAgo = new Date();
              sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
              filterDate = Timestamp.fromDate(sevenDaysAgo);
            }

            const winsQuery = query(
              collection(db, COLLECTION_NAMES.WINS_EXCITED),
              where('createdAt', '>=', filterDate),
              orderBy('createdAt', 'desc')
            );
            const winsSnapshot = await getDocs(winsQuery);
            
            if (!winsSnapshot.empty) {
              const winsData = winsSnapshot.docs.map(doc => {
                const data = doc.data();
                const user = users.find(u => u.id === data.userId);
                return `${user ? user.name : 'Unknown User'}: [${data.type}] ${data.content}`;
              }).join('\n');

              if (winsData) {
                await addIndividualNote(
                  'segue',
                  `Auto-populated Highlights:\n${winsData}`,
                  'general',
                  meetingDate,
                  meetingTimestamp || activeMeeting.meetingTimestamp.toDate().toISOString()
                );
                toast({
                  title: 'Segue Populated',
                  description: 'Recent Wins & Excited items have been added to notes.',
                  status: 'info',
                  duration: 3000,
                });
              }
            }
          } catch (error) {
            console.error('Error populating segue:', error);
          }
        }
      }
    };
    
    populateSegue();
  }, [
    meetingStarted, 
    activeMeeting, 
    currentSectionIndex, 
    isPresenter, 
    users, 
    addIndividualNote, 
    meetingDate, 
    meetingTimestamp, 
    structuredNotes.individualNotes.segue, 
    toast,
    loadingMeetings,
    savedMeetings
  ]);

  useEffect(() => {
    if (structuredNotes.sectionNotes && !hasUnsavedChanges && !isSaving) {
      isLoadingInitialNotes.current = true;
      
      // Load presenter notes from sectionNotes
      if (structuredNotes.sectionNotes.presenter) {
        setPresenterNotes(structuredNotes.sectionNotes.presenter);
        lastSyncedPresenterNotesRef.current = structuredNotes.sectionNotes.presenter;
        setSectionNotes({ presenter: structuredNotes.sectionNotes.presenter });
      }

      setMeetingSaved(true);
      setHasUnsavedChanges(false);
      setTimeout(() => {
        isLoadingInitialNotes.current = false;
      }, 0);
    }
  }, [structuredNotes, hasUnsavedChanges, isSaving]);

  useEffect(() => {
    const loadIssues = async () => {
      try {
        const issuesQuery = query(
          collection(db, COLLECTION_NAMES.ISSUES),
          where('status', '!=', 'solved')
        );
        const issuesSnapshot = await getDocs(issuesQuery);
        const issuesData = issuesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Issue[];
        setAllIssues(issuesData);
      } catch (error) {
        console.error('Error loading issues:', error);
      }
    };
    loadIssues();

    const storedTimestamp = sessionStorage.getItem('l10_meeting_timestamp');
    if (!storedTimestamp) {
      sessionStorage.removeItem('l10_draft_notes');
      sessionStorage.removeItem('l10_draft_presenter_notes');
    }
  }, []);

  useEffect(() => {
    if (!meetingStarted && !meetingTimestamp) {
      sessionStorage.removeItem('l10_draft_section_notes');
      sessionStorage.removeItem('l10_draft_presenter_notes');
      sessionStorage.removeItem('l10_meeting_timestamp');
      setSectionNotes({});
      setPresenterNotes('');
    }
  }, [meetingStarted, meetingTimestamp]);

  useEffect(() => {
    const refreshIssuesForIDS = async () => {
      const idsIndex = sections.findIndex((s) => s.key === 'ids');
      if (currentSectionIndex === idsIndex && meetingStarted) {
        await autoAddOpenIssues();
      }
    };
    refreshIssuesForIDS();
  }, [currentSectionIndex, meetingStarted, autoAddOpenIssues]);

  // Real-time listener for tangent alerts - shows modal to all participants
  useEffect(() => {
    if (!activeMeeting?.id) {
      return;
    }

    const tangentAlertsRef = collection(db, COLLECTION_NAMES.TANGENT_ALERTS);
    const q = query(
      tangentAlertsRef,
      where('meetingId', '==', activeMeeting.id),
      orderBy('createdAt', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (!snapshot.empty) {
          const latestAlert = snapshot.docs[0];
          const alertId = latestAlert.id;
          const alertData = latestAlert.data();
          
          // Only show modal if this alert hasn't been dismissed by this user
          if (!dismissedTangentAlerts.has(alertId)) {
            setTangentTriggeredByUserId(alertData.triggeredByUserId);
            setShowTangentModal(true);
          }
        }
      },
      (error) => {
        console.error('Error listening to tangent alerts:', error);
      }
    );

    return () => unsubscribe();
  }, [activeMeeting?.id, dismissedTangentAlerts]);

  useEffect(() => {
    // Only store presenter notes in session storage
    if (presenterNotes) {
      sessionStorage.setItem('l10_draft_section_notes', JSON.stringify({ presenter: presenterNotes }));
    }
  }, [presenterNotes]);

  useEffect(() => {
    if (meetingTimestamp) {
      sessionStorage.setItem('l10_meeting_timestamp', meetingTimestamp);
    }
  }, [meetingTimestamp]);

  useEffect(() => {
    if (presenterNotes) {
      sessionStorage.setItem('l10_draft_presenter_notes', presenterNotes);
    }
  }, [presenterNotes]);

  // Removed - no longer using single textarea

  useEffect(() => {
    if (!meetingStarted || !hasUnsavedChanges || isSaving) return;

    const autoSaveInterval = setInterval(async () => {
      if (!hasUnsavedChanges || isSaving) return;

      try {
        const timestamp = meetingTimestamp || new Date().toISOString();
        const presenterNotesObj = { presenter: presenterNotes };

        await saveNotes(presenterNotesObj, structuredNotes.individualNotes, meetingDate, timestamp);

        setSelectedMeetingTimestamp(timestamp);
        sessionStorage.setItem('l10_meeting_timestamp', timestamp);

        setMeetingSaved(true);
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error('Auto-save failed:', error);
      }
    }, 10000);

    return () => clearInterval(autoSaveInterval);
  }, [
    meetingStarted,
    hasUnsavedChanges,
    isSaving,
    meetingTimestamp,
    sectionNotes,
    presenterNotes,
    structuredNotes.individualNotes,
    meetingDate,
    saveNotes,
  ]);

  const offTrackMetrics = useMemo(() => {
    const now = new Date();
    const currentQuarter = Math.floor(now.getMonth() / 3) + 1;
    const currentYear = now.getFullYear();

    return metrics.filter((metric) => {
      const metricYear = metric.year || currentYear;
      const metricQuarter = metric.quarter
        ? parseInt(metric.quarter.replace('Q', ''))
        : currentQuarter;

      if (metricYear !== currentYear || metricQuarter !== currentQuarter) {
        return false;
      }

      // Simplified: Check if metric is off-track based on goals
      // This would need to be enhanced based on actual metric goal logic
      return false; // Placeholder
    });
  }, [metrics]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentSection = sections[currentSectionIndex];
  const isOverTime = sectionElapsedSeconds > currentSection.duration * 60;

  // Rate limiting for tangent alerts (3 per user per meeting)
  const TANGENT_LIMIT_PER_MEETING = 3;

  const checkTangentRateLimit = async (userId: string, meetingId: string): Promise<{ allowed: boolean; reason?: string }> => {
    try {
      const tangentAlertsRef = collection(db, COLLECTION_NAMES.TANGENT_ALERTS);
      const q = query(
        tangentAlertsRef,
        where('meetingId', '==', meetingId),
        where('triggeredByUserId', '==', userId)
      );
      const querySnapshot = await getDocs(q);
      const count = querySnapshot.size;

      if (count >= TANGENT_LIMIT_PER_MEETING) {
        return {
          allowed: false,
          reason: `You have reached the limit of ${TANGENT_LIMIT_PER_MEETING} tangent alerts per meeting.`,
        };
      }

      return { allowed: true };
    } catch (error) {
      console.error('Error checking tangent rate limit:', error);
      // On error, allow the request (fail open)
      return { allowed: true };
    }
  };

  const handleTangentClick = async () => {
    if (!activeMeeting || !currentUser?.uid) {
      return;
    }

    // Check rate limit
    const rateLimitCheck = await checkTangentRateLimit(currentUser.uid, activeMeeting.id);
    if (!rateLimitCheck.allowed) {
      toast({
        title: 'Rate Limit Exceeded',
        description: rateLimitCheck.reason || 'You have reached the limit for tangent alerts.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    try {
      // Create tangent alert document
      const tangentAlertData: Omit<TangentAlert, 'id'> = {
        meetingId: activeMeeting.id,
        meetingTimestamp: activeMeeting.meetingTimestamp,
        triggeredByUserId: currentUser.uid,
        createdAt: Timestamp.now(),
      };

      const tangentAlertsRef = collection(db, COLLECTION_NAMES.TANGENT_ALERTS);
      await addDoc(tangentAlertsRef, tangentAlertData);

      // The modal will be shown automatically via the real-time listener
      // No need to manually set showTangentModal here

      // Log activity (via cloud function if available, otherwise skip)
      // Activity logging is handled server-side via Cloud Functions
    } catch (error) {
      console.error('Error creating tangent alert:', error);
      toast({
        title: 'Error',
        description: 'Failed to trigger tangent alert. Please try again.',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleStartMeeting = async () => {
    if (!canPresent) {
      toast({
        title: 'Permission Denied',
        description: 'You do not have permission to start meetings. Contact an administrator.',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (activeMeeting && !isPresenter) {
      toast({
        title: 'Meeting In Progress',
        description: 'A meeting is already in progress, presented by another user.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (offTrackMetrics.length > 0) {
      setShowOffTrackModal(true);
    } else {
      await proceedWithMeetingStart();
    }
  };

  const proceedWithMeetingStart = async () => {
    try {
      await startActiveMeeting(meetingDate);
      setMeetingSaved(false);
      setHasUnsavedChanges(true);
      // Clear dismissed tangent alerts when a new meeting starts
      setDismissedTangentAlerts(new Set());
      sessionStorage.removeItem('dismissed_tangent_alerts');
      await autoAddOpenIssues();
    } catch (error) {
      console.error('Error starting meeting:', error);
      toast({
        title: 'Error',
        description: 'Failed to start meeting. Please try again.',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleAddOffTrackMetrics = async (metricIds: string[]) => {
    const timestamp = new Date().toISOString();
    await saveOffTrackMetrics(timestamp, metricIds);
    proceedWithMeetingStart();
  };

  const handleSkipOffTrackMetrics = () => {
    proceedWithMeetingStart();
  };

  const toggleRockExpanded = async (rockId: string) => {
    const wasExpanded = expandedRocks.has(rockId);
    setExpandedRocks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rockId)) {
        newSet.delete(rockId);
      } else {
        newSet.add(rockId);
      }
      return newSet;
    });

    // Load data when expanding
    if (!wasExpanded && !rockDetails[rockId]) {
      setRockDetails((prev) => ({
        ...prev,
        [rockId]: { milestones: [], actionItems: [], notes: [], loading: true },
      }));

      try {
        const [milestones, actionItems, notes] = await Promise.all([
          loadMilestonesForRock(rockId),
          loadActionItemsForRock(rockId),
          loadPreviousNotes(rockId),
        ]);

        setRockDetails((prev) => ({
          ...prev,
          [rockId]: {
            milestones: milestones.sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()),
            actionItems: actionItems.filter((ai) => !ai.complete && !ai.dropped),
            notes: notes,
            loading: false,
          },
        }));
      } catch (error) {
        console.error('Error loading rock details:', error);
        setRockDetails((prev) => ({
          ...prev,
          [rockId]: { milestones: [], actionItems: [], notes: [], loading: false },
        }));
      }
    }
  };

  const handleEditCounters = (
    rockId: string,
    rockName: string,
    consecutive: number,
    total: number
  ) => {
    setEditingRockId(rockId);
    setEditingRockName(rockName);
    setEditingConsecutive(consecutive);
    setEditingTotal(total);
    setShowEditCountersModal(true);
  };

  const handleSaveCounters = async (consecutive: number, total: number) => {
    if (!editingRockId) return;

    try {
      await updateRock(editingRockId, {
        off_track_consecutive_count: consecutive,
        off_track_total_count: total,
      } as any);
      setShowEditCountersModal(false);
      setEditingRockId(null);
    } catch (error) {
      console.error('Error updating counters:', error);
      throw error;
    }
  };

  const nextSection = async () => {
    if (!isPresenter) return;

    if (currentSectionIndex < sections.length - 1) {
      try {
        await updateSection(currentSectionIndex + 1);

        setTimeout(() => {
          const sectionContainer = document.querySelector('[data-section-container="true"]');
          if (sectionContainer) {
            const containerTop = sectionContainer.getBoundingClientRect().top + window.pageYOffset;
            const offset = 300;
            window.scrollTo({
              top: containerTop - offset,
              behavior: 'smooth'
            });
          }
        }, 150);
      } catch (error) {
        console.error('Error updating section:', error);
        toast({
          title: 'Error',
          description: 'Failed to update section.',
          status: 'error',
          duration: 3000,
        });
      }
    }
  };

  const executeStopMeeting = async () => {
    setIsSaving(true);
    try {
      const timestamp = meetingTimestamp || new Date().toISOString();
      const presenterNotesObj = { presenter: presenterNotes };

      // Save the notes first (only presenter in sectionNotes, all others as individual notes)
      await saveNotes(presenterNotesObj, structuredNotes.individualNotes, meetingDate, timestamp, true);

      // Stop the active meeting (this will delete the active_meetings document and trigger the summary generation)
      await stopActiveMeeting();

      // Clear the meeting timestamp and notes after successful stop
      setSelectedMeetingTimestamp(null);
      sessionStorage.removeItem('l10_meeting_timestamp');

      setMeetingSaved(true);
      setHasUnsavedChanges(false);
      
      // Clear notes after successful stop
      setSectionNotes({});
      setPresenterNotes('');
      sessionStorage.removeItem('l10_draft_section_notes');
      sessionStorage.removeItem('l10_draft_presenter_notes');

      // Clear refs to match cleared state
      lastSyncedPresenterNotesRef.current = '';
      if (presenterNotesRef.current) {
        presenterNotesRef.current.value = '';
      }

      toast({
        title: 'Meeting completed',
        description: 'Meeting has been stopped and summary will be generated automatically.',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error stopping meeting:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to stop meeting. Please try again.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleStopMeeting = async () => {
    if (isSaving || !meetingStarted || !isPresenter) return;

    const hasContent =
      presenterNotes.trim() !== '' ||
      Object.values(structuredNotes.individualNotes).flat().length > 0;

    if (!hasContent) {
      setShowStopMeetingModal(true);
      return;
    }

    await executeStopMeeting();
  };

  const handleDeleteMeeting = (timestamp: string) => {
    setDeleteTimestamp(timestamp);
    setShowDeleteModal(true);
  };

  const confirmDeleteMeeting = async () => {
    if (deleteTimestamp) {
      await deleteMeeting(deleteTimestamp);
      if (deleteTimestamp === meetingTimestamp) {
        clearMeetingData();
      }
      setShowDeleteModal(false);
      setDeleteTimestamp(null);
    }
  };

  const clearMeetingData = () => {
    setSelectedMeetingTimestamp(null);
    setSectionNotes({});
    setPresenterNotes('');
    setMeetingSaved(false);
    setHasUnsavedChanges(false);
    sessionStorage.removeItem('l10_draft_section_notes');
    sessionStorage.removeItem('l10_draft_presenter_notes');
    sessionStorage.removeItem('l10_meeting_timestamp');
  };

  // Map meeting section keys to note section keys
  const getNoteSectionKey = (sectionKey: string): string => {
    const sectionMap: Record<string, string> = {
      segue: 'segue',
      scorecard: 'scorecard',
      customer_employee_headlines: 'customer_employee_headlines',
      action_item_list: 'customer_employee_headlines', // Action Item notes go to customer/employee section
      conclude: 'conclude',
    };
    return sectionMap[sectionKey] || 'segue'; // Default to segue if not mapped
  };

  const appendSectionNote = async (
    sectionLabel: string,
    note: string,
    category: 'general' | 'department_communication' = 'general',
    owners?: string[],
    departments?: string[]
  ) => {
    if (!note.trim() || !meetingTimestamp) return;

    if (category === 'department_communication' && (!owners || owners.length === 0) && (!departments || departments.length === 0)) {
      toast({
        title: 'Error',
        description: 'Department communication notes require at least one owner or department.',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    // Find the section key from the label
    const section = sections.find((s) => s.label === sectionLabel);
    if (!section) return;

    const noteSectionKey = getNoteSectionKey(section.key);

    try {
      await addIndividualNote(
        noteSectionKey,
        note.trim(),
        category,
        meetingDate,
        meetingTimestamp,
        owners,
        departments
      );
    setMeetingSaved(false);
    setHasUnsavedChanges(true);
      
      // Reset form
      setSectionNote('');
      setSectionNoteCategory('general');
      setSectionNoteOwners(new Set());
      setSectionNoteDepartments(new Set());
    } catch (error) {
      console.error('Error adding section note:', error);
      toast({
        title: 'Error',
        description: 'Failed to add note. Please try again.',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleAddSectionNote = async () => {
    if (sectionNote.trim() && currentSection) {
      // For conclude section, always use 'general' category
      const category = currentSection.key === 'conclude' ? 'general' : sectionNoteCategory;
      const owners = category === 'department_communication' ? Array.from(sectionNoteOwners) : undefined;
      const depts = category === 'department_communication' ? Array.from(sectionNoteDepartments) : undefined;
      await appendSectionNote(currentSection.label, sectionNote, category, owners, depts);
    }
  };

  const handleToggleSectionNoteOwner = (userId: string) => {
    const newSet = new Set(sectionNoteOwners);
    if (newSet.has(userId)) {
      newSet.delete(userId);
    } else {
      newSet.add(userId);
    }
    setSectionNoteOwners(newSet);
  };

  const handleToggleSectionNoteDepartment = (deptId: string) => {
    const newSet = new Set(sectionNoteDepartments);
    if (newSet.has(deptId)) {
      newSet.delete(deptId);
    } else {
      newSet.add(deptId);
    }
    setSectionNoteDepartments(newSet);
  };

  const handleAddActionItemToNotes = (actionItem: any) => {
    setActionItemForNote(actionItem);
    setActionItemNoteText('');
    setShowAddActionItemNoteModal(true);
  };

  const handleSaveActionItemNote = async () => {
    if (actionItemNoteText.trim()) {
      await appendSectionNote('Action Items', actionItemNoteText.trim());
      setShowAddActionItemNoteModal(false);
      setActionItemForNote(null);
      setActionItemNoteText('');
    }
  };


  // Handler for inline issue resolution - opens modal for all resolutions
  // Checks if it's the last issue BEFORE opening modal
  const handleResolveIssue = useCallback(async (issueId: string, issueName: string) => {
    try {
      // Find the issue to get its relatedRockId
      const issue = allIssues.find((i) => i.id === issueId);
      const relatedRockId = issue?.relatedRockId;

      // Check if this is the last issue BEFORE opening modal
      let isLastIssue = false;
      if (relatedRockId) {
        const openIssues = await getOpenIssuesForRock(relatedRockId);
        const otherOpenIssues = openIssues.filter((i) => i.id !== issueId);
        isLastIssue = otherOpenIssues.length === 0;
      }

      // Store data and open modal
      setResolvingIssue({ issueId, issueName, relatedRockId, isLastIssue });
      setResolveModalOpen(true);
    } catch (error) {
      console.error('Error preparing to resolve issue:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to prepare issue resolution',
        status: 'error',
        duration: 3000,
      });
    }
  }, [allIssues, toast]);

  // Handler for normal issue resolution (not last issue)
  const handleConfirmResolve = useCallback(async (resolutionNotes: string) => {
    if (!resolvingIssue) return;

    try {
      await resolveIssue(resolvingIssue.issueId, resolutionNotes);
      
      // Trigger refresh for the rock if it had a related rock
      if (resolvingIssue.relatedRockId) {
        setRockRefreshTrigger((prev) => ({
          ...prev,
          [resolvingIssue.relatedRockId!]: (prev[resolvingIssue.relatedRockId!] || 0) + 1,
        }));
      }

      setResolveModalOpen(false);
      setResolvingIssue(null);
    } catch (error) {
      console.error('Error resolving issue:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to resolve issue',
        status: 'error',
        duration: 3000,
      });
    }
  }, [resolvingIssue, resolveIssue, toast]);

  // Handler for last issue resolution - resolves and marks rock on-track
  const handleMarkOnTrack = useCallback(async (resolutionNotes: string) => {
    if (!resolvingIssue) return;

    try {
      // Resolve the issue
      await actuallyResolveIssue(resolvingIssue.issueId, resolutionNotes);
      
      // Trigger refresh for the rock
      if (resolvingIssue.relatedRockId) {
        setRockRefreshTrigger((prev) => ({
          ...prev,
          [resolvingIssue.relatedRockId!]: (prev[resolvingIssue.relatedRockId!] || 0) + 1,
        }));
      }

      toast({
        title: 'Success',
        description: 'Issue resolved. Rock is now on-track.',
        status: 'success',
        duration: 3000,
      });

      setResolveModalOpen(false);
      setResolvingIssue(null);
    } catch (error) {
      console.error('Error resolving issue and marking rock on-track:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to resolve issue and mark rock as on-track',
        status: 'error',
        duration: 3000,
      });
    }
  }, [resolvingIssue, actuallyResolveIssue, toast]);

  const filteredRocks = useMemo(() => {
    let filtered = rocks;

    if (selectedUserId !== 'all') {
      filtered = filtered.filter((rock) => rock.ownerId === selectedUserId);
    }

    if (rockTypeFilter !== 'all') {
      filtered = filtered.filter((rock) => rock.rockType === rockTypeFilter);
    }

    return filtered;
  }, [rocks, selectedUserId, rockTypeFilter]);

  // Helper functions for showing modals
  const showOutstandingIssues = useCallback((issueCount: number) => {
    setOutstandingIssuesCount(issueCount);
    setShowOutstandingIssuesModal(true);
  }, []);

  const handleRockStatusUpdate = useCallback(
    async (rockId: string, newStatus: 'on-track' | 'off-track' | 'at-risk') => {
      const rock = rocks.find((r) => r.id === rockId);
      if (!rock) {
        console.error('Rock not found:', rockId);
        return;
      }

      // Only validate on-track and off-track status changes (status is computed from issues)
      if (newStatus === 'on-track' || newStatus === 'off-track') {
        // Get open issues to determine current computed status
        const openIssues = await getOpenIssuesForRock(rockId);
        
        // Handle UI flow based on requested status change
        if (newStatus === 'off-track') {
          // If rock has no issues, show Add Issue modal with special button text
          if (openIssues.length === 0) {
            setRockForIssueRequired({ id: rockId, name: rock.rock });
            setPendingRockStatusChange({ rockId, newStatus: 'off-track' });
            setAddIssueModalButtonText('Add Issue and Move Rock to Off-Track');
            setAddIssueModalContext('moving-to-off-track');
            setShowAddIssueModal(true);
          }
          // If rock already has issues, it's already off-track (computed) - no-op
        } else if (newStatus === 'on-track') {
          // If rock has open issues, show OutstandingIssuesModal
          if (openIssues.length > 0) {
            showOutstandingIssues(openIssues.length);
          }
          // If rock has no issues, it's already on-track (computed) - no-op
        }
      } else {
        // For at-risk, update directly (not computed from issues)
        const previousStatus = rock.status;
        const updateTimestamp = Date.now();

        setLastStatusChange({
          rockId,
          previousStatus,
          newStatus,
          timestamp: updateTimestamp,
        });

        try {
          await updateRock(rockId, { status: newStatus } as any);

          setTimeout(() => {
            setLastStatusChange((prev) =>
              prev?.rockId === rockId && prev?.timestamp === updateTimestamp ? null : prev
            );
          }, 10000);
        } catch (error) {
          console.error('Error updating rock status:', error);
          const errorMessage = error instanceof Error ? error.message : 'Failed to update rock status. Please try again.';
          toast({
            title: 'Error',
            description: errorMessage,
            status: 'error',
            duration: 3000,
          });
        }
      }
    },
    [rocks, updateRock, toast, showOutstandingIssues]
  );

  const handleUndoStatusChange = useCallback(async () => {
    if (!lastStatusChange) return;

    const { rockId, previousStatus } = lastStatusChange;

    try {
      await updateRock(rockId, { status: previousStatus } as any);
      setLastStatusChange(null);
    } catch (error) {
      console.error('Error undoing status change:', error);
      toast({
        title: 'Error',
        description: 'Failed to undo status change. Please try again.',
        status: 'error',
        duration: 3000,
      });
    }
  }, [lastStatusChange, updateRock, toast]);


  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <Container maxW="container.xl" py={8}>
      <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6}>
        <GridItem>
          <Box
            bg={bgColor}
            borderWidth={1}
            borderColor={borderColor}
            borderRadius="lg"
            p={4}
            position="sticky"
            top="90px"
            zIndex={50}
            boxShadow="sm"
          >
            <VStack spacing={3} align="stretch">
              <HStack justify="space-between">
                <HStack spacing={3}>
                  <Heading size="xl">L10 Meeting</Heading>
                  {meetingStarted && (
                    <Badge
                      colorScheme="blue"
                      px={3}
                      py={1}
                      borderRadius="lg"
                      display="flex"
                      alignItems="center"
                      gap={2}
                    >
                      <Box w={2} h={2} bg="blue.500" borderRadius="full" />
                      <Text fontSize="sm" fontWeight="medium">
                        {isPresenter
                          ? 'You are presenting this meeting'
                          : 'Viewing active meeting'}
                      </Text>
                      {!isPresenter && activeMeeting && (
                        <Text fontSize="xs" color="blue.700" ml={2}>
                          Updates every 3 seconds
                        </Text>
                      )}
                    </Badge>
                  )}
                </HStack>
                {meetingStarted && (
                  <VStack align="end" spacing={0}>
                    <Text fontSize="xs" color="gray.600">
                      Total Time
                    </Text>
                    <Text fontSize="xl" fontWeight="bold">
                      {formatTime(totalElapsedSeconds)}
                    </Text>
                  </VStack>
                )}
              </HStack>

              <HStack spacing={3} flexWrap="wrap">
                <HStack>
                  <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
                    Date:
                  </Text>
                  <Input
                    type="date"
                    value={meetingDate}
                    onChange={(e) => setMeetingDate(e.target.value)}
                    isDisabled={meetingStarted}
                    size="sm"
                    maxW="150px"
                  />
                </HStack>

                <HStack>
                  <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
                    Load:
                  </Text>
                  <Select
                    value={meetingTimestamp || ''}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (hasUnsavedChanges) {
                        const confirmSwitch = window.confirm(
                          'You have unsaved changes. Are you sure you want to switch meetings? Your changes will be lost.'
                        );
                        if (!confirmSwitch) {
                          return;
                        }
                      }

                      setSelectedMeetingTimestamp(value || null);
                      setHasUnsavedChanges(false);
                      if (!value) {
                        clearMeetingData();
                      }
                    }}
                    isDisabled={meetingStarted || loadingMeetings}
                    size="sm"
                    maxW="200px"
                  >
                    <option value="">
                      {loadingMeetings ? 'Loading meetings...' : 'New Meeting'}
                    </option>
                    {!loadingMeetings &&
                      savedMeetings.map((meeting) => (
                        <option key={meeting.meeting_timestamp} value={meeting.meeting_timestamp}>
                          {meeting.display_name}
                        </option>
                      ))}
                  </Select>
                </HStack>

                <HStack ml="auto" spacing={2}>
                  {!meetingStarted ? (
                    <Button
                      onClick={handleStartMeeting}
                      colorScheme="blue"
                      size="sm"
                      isDisabled={meetingLoading || (activeMeeting && !isPresenter) || !canPresent}
                      leftIcon={<Icon as={Play} w={4} h={4} />}
                    >
                      {activeMeeting && !isPresenter
                        ? 'Meeting In Progress'
                        : meetingLoading
                        ? 'Loading...'
                        : 'Start'}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleStopMeeting}
                      colorScheme="red"
                      size="sm"
                      isDisabled={isSaving || !isPresenter}
                      leftIcon={<Icon as={Square} w={4} h={4} />}
                    >
                      {isSaving ? 'Stopping...' : 'Stop'}
                    </Button>
                  )}

                  {meetingTimestamp && (
                    <Button
                      onClick={() => handleDeleteMeeting(meetingTimestamp)}
                      colorScheme="red"
                      size="sm"
                      isDisabled={meetingStarted}
                      leftIcon={<Icon as={Trash2} w={4} h={4} />}
                    >
                      Delete
                    </Button>
                  )}
                </HStack>
              </HStack>

              {meetingStarted && (
                <VStack spacing={2} align="stretch">
                  <Box position="relative" h={8} bg="gray.200" borderRadius="lg" overflow="hidden">
                    <Box
                      position="absolute"
                      insetY={0}
                      left={0}
                      bg={isOverTime ? 'red.500' : 'blue.500'}
                      transition="width 0.3s"
                      w={`${Math.min(
                        (sectionElapsedSeconds / (currentSection.duration * 60)) * 100,
                        100
                      )}%`}
                    />
                  </Box>
                  <HStack justify="space-between">
                    <VStack align="start" spacing={0}>
                      <Text fontSize="xs" color="gray.600" whiteSpace="nowrap">
                        Section {currentSectionIndex + 1} of {sections.length}:{' '}
                        {currentSection.label}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Allocated: {currentSection.duration} minutes
                      </Text>
                    </VStack>
                    {currentSectionIndex < sections.length - 1 && (
                      <HStack>
                        <Button
                          onClick={handleTangentClick}
                          colorScheme="red"
                          size="sm"
                          variant="outline"
                          leftIcon={<Icon as={AlertTriangle} w={4} h={4} />}
                        >
                          Tangent
                        </Button>
                        <Button
                          onClick={nextSection}
                          colorScheme="blue"
                          size="sm"
                          isDisabled={!isPresenter}
                        >
                          Next Section
                        </Button>
                      </HStack>
                    )}
                  </HStack>
                </VStack>
              )}
            </VStack>
          </Box>

          {meetingStarted && (
            <Box mt={6}>
              {currentSection.key === 'rock_review' && (
                <Box
                  bg={bgColor}
                  borderWidth={1}
                  borderColor={borderColor}
                  borderRadius="lg"
                  data-section-container="true"
                >
                  <Box
                    position="sticky"
                    top="280px"
                    bg={bgColor}
                    zIndex={30}
                    px={6}
                    pt={6}
                    pb={4}
                    borderBottomWidth={1}
                    borderBottomColor={borderColor}
                    borderRadius="lg"
                    boxShadow="sm"
                  >
                    <HStack justify="space-between">
                      <Badge
                        fontSize="2xl"
                        fontWeight="bold"
                        px={4}
                        py={2}
                        borderRadius="lg"
                        colorScheme={isOverTime ? 'red' : 'blue'}
                      >
                        Rock Review
                      </Badge>
                      <HStack spacing={4}>
                        <VStack align="end" spacing={1}>
                          <Text fontSize="xs" color="gray.600">
                            User:
                          </Text>
                          <Select
                            value={selectedUserId}
                            onChange={async (e) => {
                              if (!isPresenter) return;
                              try {
                                await updateFilters(e.target.value, 'all');
                              } catch (error) {
                                console.error('Error updating filter:', error);
                              }
                            }}
                            isDisabled={usersLoading || !isPresenter}
                            size="sm"
                            minW="150px"
                          >
                            {usersLoading ? (
                              <option>Loading users...</option>
                            ) : (
                              <>
                                <option value="all">All Users</option>
                                {users.map((user) => (
                                  <option key={user.id} value={user.id}>
                                    {user.name}
                                  </option>
                                ))}
                              </>
                            )}
                          </Select>
                        </VStack>
                        <VStack align="end" spacing={1}>
                          <Text fontSize="xs" color="gray.600">
                            Rock Type:
                          </Text>
                          <Select
                            value={rockTypeFilter}
                            onChange={async (e) => {
                              if (!isPresenter) return;
                              try {
                                await updateRockTypeFilter(
                                  e.target.value as 'all' | 'company' | 'individual'
                                );
                              } catch (error) {
                                console.error('Error updating rock type filter:', error);
                              }
                            }}
                            isDisabled={!isPresenter}
                            size="sm"
                            minW="150px"
                          >
                            <option value="all">All Rocks</option>
                            <option value="company">Company Rocks</option>
                            <option value="individual">Individual Rocks</option>
                          </Select>
                        </VStack>
                      </HStack>
                    </HStack>
                  </Box>

                  <Box px={6} pb={6} pt={20}>
                    {filteredRocks.length === 0 &&
                    (selectedUserId !== 'all' || rockTypeFilter !== 'all') ? (
                      <Box textAlign="center" py={12}>
                        <Text fontSize="lg" fontWeight="medium" color="gray.500" mb={2}>
                          No rocks found
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          Try adjusting the filters above
                        </Text>
                      </Box>
                    ) : (
                      <VStack spacing={4} align="stretch">
                        {filteredRocks.map((rock) => {
                          const owner = users.find((u) => u.id === rock.ownerId);
                          return (
                            <RockReviewCard
                              key={rock.id}
                              rock={rock}
                              owner={owner}
                              onStatusChange={handleRockStatusUpdate}
                              loadMilestonesForRock={loadMilestonesForRock}
                              loadIssuesForRock={loadIssuesForRock}
                              loadActionItemsForRock={loadActionItemsForRock}
                              toggleMilestone={toggleMilestone}
                              onResolveIssue={handleResolveIssue}
                              isPresenter={isPresenter}
                              refreshTrigger={rockRefreshTrigger[rock.id] || 0}
                            />
                          );
                        })}
                      </VStack>
                    )}
                  </Box>
                </Box>
              )}

              {currentSection.key === 'ids' && (
                <Box
                  bg={bgColor}
                  borderWidth={1}
                  borderColor={borderColor}
                  borderRadius="lg"
                  data-section-container="true"
                >
                  <Box
                    position="sticky"
                    top="280px"
                    bg={bgColor}
                    zIndex={30}
                    px={6}
                    pt={6}
                    pb={4}
                    borderBottomWidth={1}
                    borderBottomColor={borderColor}
                    borderRadius="lg"
                  >
                    <Badge
                      fontSize="2xl"
                      fontWeight="bold"
                      px={4}
                      py={2}
                      borderRadius="lg"
                      colorScheme={isOverTime ? 'red' : 'blue'}
                    >
                      IDS (Identify, Discuss, Solve)
                    </Badge>
                  </Box>
                  <Box pt={6}>
                    <HStack justify="space-between" mb={4} px={6}>
                      <Heading size="lg">Off-Track Rocks</Heading>
                      {getFlaggedCount() > 0 && (
                        <Text fontSize="sm" color="gray.600">
                          {getFlaggedCount()} of 3 selected for discussion
                        </Text>
                      )}
                    </HStack>
                    {filteredRocks.filter((r) => r.status === 'off-track').length > 0 ? (
                      <VStack spacing={3} px={6} align="stretch">
                        {filteredRocks
                          .filter((r) => r.status === 'off-track')
                          .map((rock) => {
                            const owner = users.find((u) => u.id === rock.ownerId);
                            const rockIssues = meetingIssues
                              .filter(
                                (mi) =>
                                  mi.issue?.relatedRockId === rock.id &&
                                  mi.issue?.status === 'open'
                              )
                              .map((mi) => mi.issue)
                              .filter((issue): issue is Issue => issue !== undefined);
                            const flagged = isFlagged(rock.id);
                            const reviewed = isReviewed(rock.id);
                            const isExpanded = expandedRocks.has(rock.id);

                            return (
                              <Box key={rock.id} w="full">
                                <Box
                                  borderWidth={2}
                                  borderRadius="lg"
                                  borderColor={flagged ? 'blue.400' : 'red.200'}
                                  bg={flagged ? 'blue.50' : 'red.50'}
                                  p={4}
                                  w="full"
                                >
                                  <HStack justify="space-between" align="start">
                                    <HStack spacing={2} flex={1}>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => toggleRockExpanded(rock.id)}
                                      >
                                        <Icon
                                          as={isExpanded ? ChevronDown : ChevronRight}
                                          w={5}
                                          h={5}
                                        />
                                      </Button>
                                      <Text
                                        fontSize="2xl"
                                        fontWeight="bold"
                                        color={flagged ? 'blue.600' : 'red.600'}
                                      >
                                        {flagged ? '🔵' : '🔴'}
                                      </Text>
                                      <VStack align="start" spacing={0} flex={1}>
                                        <HStack spacing={2} flexWrap="wrap">
                                          <Text fontWeight="medium">{rock.rock}</Text>
                                          {owner && (
                                            <Text fontSize="sm" fontWeight="bold" color="gray.600">
                                              - {owner.name}
                                            </Text>
                                          )}
                                          {reviewed && (
                                            <Badge colorScheme="green">✓ Reviewed</Badge>
                                          )}
                                        </HStack>
                                      </VStack>
                                    </HStack>

                                    <HStack spacing={3}>
                                      {(rock as any).off_track_consecutive_count > 0 ||
                                      (rock as any).off_track_total_count > 0 ? (
                                        <HStack spacing={2}>
                                          <VStack spacing={0}>
                                            <Text fontSize="10px" fontWeight="semibold" color="gray.500" textTransform="uppercase">
                                              Consecutive
                                            </Text>
                                            <Badge colorScheme="orange" fontSize="xs" px={2.5} py={1}>
                                              {(rock as any).off_track_consecutive_count || 0}
                                            </Badge>
                                          </VStack>
                                          <Text color="gray.400" fontSize="lg">/</Text>
                                          <VStack spacing={0}>
                                            <Text fontSize="10px" fontWeight="semibold" color="gray.500" textTransform="uppercase">
                                              Total
                                            </Text>
                                            <Badge colorScheme="red" fontSize="xs" px={2.5} py={1}>
                                              {(rock as any).off_track_total_count || 0}
                                            </Badge>
                                          </VStack>
                                          {isPresenter && (
                                            <Button
                                              size="xs"
                                              variant="ghost"
                                              onClick={() =>
                                                handleEditCounters(
                                                  rock.id,
                                                  rock.rock,
                                                  (rock as any).off_track_consecutive_count || 0,
                                                  (rock as any).off_track_total_count || 0
                                                )
                                              }
                                            >
                                              <Icon as={Edit2} w={4} h={4} />
                                            </Button>
                                          )}
                                        </HStack>
                                      ) : null}
                                      {isPresenter && !reviewed && (
                                        <Button
                                          variant={flagged ? 'ghost' : 'outline'}
                                          size="sm"
                                          onClick={() => toggleFlagged(rock.id)}
                                          isDisabled={!flagged && getFlaggedCount() >= 3}
                                          leftIcon={<Icon as={Flag} w={4} h={4} />}
                                        >
                                          {flagged ? 'Unflag' : 'Flag'}
                                        </Button>
                                      )}
                                    </HStack>
                                  </HStack>

                                  {isExpanded && (
                                    <Box borderTopWidth={2} borderColor="gray.200" mt={4} pt={4}>
                                      {rockDetails[rock.id]?.loading ? (
                                        <Box textAlign="center" py={4}>
                                          <Text fontSize="sm" color="gray.500">Loading...</Text>
                                        </Box>
                                      ) : (
                                        <>
                                          {/* Open Milestones */}
                                          {rockDetails[rock.id]?.milestones.filter((m) => !m.complete).length > 0 && (
                                            <Box mb={4}>
                                              <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.600" textTransform="uppercase" letterSpacing="wide">
                                                Open Milestones:
                                              </Text>
                                              <VStack spacing={1.5} align="stretch">
                                                {rockDetails[rock.id].milestones
                                                  .filter((m) => !m.complete)
                                                  .map((milestone) => (
                                                    <HStack key={milestone.id} spacing={2} fontSize="sm">
                                                      <Text color="gray.500">•</Text>
                                                      <Text color="gray.800">
                                                        {milestone.task} ({new Date(milestone.dueDate).toLocaleDateString()})
                                                      </Text>
                                                    </HStack>
                                                  ))}
                                              </VStack>
                                            </Box>
                                          )}

                                          {/* Open Action Items */}
                                          {rockDetails[rock.id]?.actionItems.length > 0 && (
                                            <Box mb={4}>
                                              <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.600" textTransform="uppercase" letterSpacing="wide">
                                                Open Action Items:
                                              </Text>
                                              <VStack spacing={1.5} align="stretch">
                                                {rockDetails[rock.id].actionItems.map((actionItem) => (
                                                  <HStack key={actionItem.id} spacing={2} fontSize="sm">
                                                    <Text color="gray.500">•</Text>
                                                    <Text color="gray.800">
                                                      {actionItem.description} {actionItem.dueDate && `(${new Date(actionItem.dueDate).toLocaleDateString()})`}
                                                    </Text>
                                                  </HStack>
                                                ))}
                                              </VStack>
                                            </Box>
                                          )}

                                          {/* Rock Notes */}
                                          {rockDetails[rock.id]?.notes.length > 0 && (
                                            <Box mb={4}>
                                              <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.600" textTransform="uppercase" letterSpacing="wide">
                                                Notes:
                                              </Text>
                                              <VStack spacing={2} align="stretch">
                                                {rockDetails[rock.id].notes.map((note) => {
                                                  const noteUser = users.find((u) => u.id === note.createdById || u.uid === note.createdById);
                                                  return (
                                                    <Box key={note.id} p={2} bg="gray.50" borderRadius="md" borderWidth={1} borderColor="gray.200">
                                                      <Text fontSize="xs" color="gray.600" mb={1}>
                                                        {noteUser?.name || 'Unknown'} - {note.createdAt?.toDate?.().toLocaleDateString() || new Date().toLocaleDateString()}
                                                      </Text>
                                                      <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap">
                                                        {note.note}
                                                      </Text>
                                                    </Box>
                                                  );
                                                })}
                                              </VStack>
                                            </Box>
                                          )}
                                        </>
                                      )}

                                      {rockIssues.length > 0 && (
                                        <Box mb={4}>
                                          <Text fontSize="sm" fontWeight="semibold" mb={2}>
                                            Related Issues
                                          </Text>
                                          <VStack spacing={1.5} align="stretch">
                                            {rockIssues.map((issue) => (
                                              <HStack key={issue.id} spacing={2} fontSize="sm">
                                                <Text color="gray.500">•</Text>
                                                <Text color="gray.800" fontWeight="medium">
                                                  {issue.name}
                                                </Text>
                                              </HStack>
                                            ))}
                                          </VStack>
                                        </Box>
                                      )}

                                      {flagged && (
                                        <RockDiscussionPanel
                                          rock={rock}
                                          owner={owner}
                                          issues={rockIssues}
                                          onAddNote={async (note) => {
                                            if (!currentUser?.uid) return;
                                            await addDoc(collection(db, COLLECTION_NAMES.ROCK_NOTES), {
                                              rockId: rock.id,
                                              note: note,
                                              createdById: currentUser.uid,
                                              createdAt: Timestamp.now(),
                                            });
                                          }}
                                          onUpdateStatus={async (newStatus) => {
                                            await updateRock(rock.id, { status: newStatus } as any);
                                          }}
                                          onCreateIssue={async (issueData) => {
                                            await createAndAddIssue({
                                              name: issueData.name,
                                              description: issueData.description,
                                              priority: issueData.priority || 'medium',
                                              relatedRockId: rock.id,
                                              isRock: true,
                                            });
                                          }}
                                          onResolveIssue={async (issueId: string) => {
                                            // Find the issue name for the handler
                                            const issue = rockIssues.find((i) => i.id === issueId);
                                            if (issue) {
                                              await handleResolveIssue(issueId, issue.name);
                                            }
                                          }}
                                          onMarkReviewed={async () => {
                                            await markAsReviewed(rock.id);
                                          }}
                                          isReviewed={reviewed}
                                        />
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                      </VStack>
                    ) : (
                      <Box
                        bg="green.50"
                        borderWidth={1}
                        borderColor="green.200"
                        borderRadius="lg"
                        p={4}
                        textAlign="center"
                        mx={6}
                      >
                        <Text color="green.800" fontWeight="medium">
                          ✓ All rocks are on track
                        </Text>
                      </Box>
                    )}
                  </Box>

                  <Box px={2} pb={6} pt={6}>
                    <Heading size="lg" mb={4} px={4}>
                      Meeting Issues
                    </Heading>
                    <MeetingIssuesList
                      meetingIssues={meetingIssues}
                      onResolve={handleResolveIssue}
                      isPresenter={isPresenter}
                    />
                  </Box>
                </Box>
              )}

              {!['rock_review', 'ids'].includes(currentSection.key) && (
                <Box
                  bg={bgColor}
                  borderWidth={1}
                  borderColor={borderColor}
                  borderRadius="lg"
                  data-section-container="true"
                >
                  <Box
                    position="sticky"
                    top="280px"
                    bg={bgColor}
                    zIndex={30}
                    px={6}
                    pt={6}
                    pb={4}
                    borderBottomWidth={1}
                    borderBottomColor={borderColor}
                    borderRadius="lg"
                  >
                    <HStack justify="space-between">
                      <Badge
                        fontSize="2xl"
                        fontWeight="bold"
                        px={4}
                        py={2}
                        borderRadius="lg"
                        colorScheme={isOverTime ? 'red' : 'blue'}
                      >
                        {currentSection.label}
                      </Badge>
                    </HStack>
                  </Box>
                  <Box px={6} pb={6} pt={6}>
                    {currentSection.key === 'action_item_list' && (
                      <L10ActionItemSection
                        users={users}
                        rocks={rocks}
                        issues={allIssues}
                        metrics={metrics}
                        meetingId={activeMeeting?.id}
                        onAddToNotes={handleAddActionItemToNotes}
                      />
                    )}
                    {currentSection.key === 'segue' && (
                      <Box py={6}>
                        <Text textAlign="center" fontSize="lg" color="gray.600" mb={6}>
                          Welcome to the L10 Meeting
                        </Text>
                        <RecentWinsExcited isPresenter={isPresenter} users={users} />
                      </Box>
                    )}
                    {currentSection.key === 'scorecard' && (
                      <Box py={6}>
                        <Text textAlign="center" fontSize="lg" color="gray.600" mb={6}>
                          Review your scorecard metrics
                        </Text>
                        
                        <ScorecardWeeklyGrid
                          metrics={metrics.filter(m => {
                            const now = new Date();
                            const q = Math.floor(now.getMonth() / 3) + 1;
                            const y = now.getFullYear();
                            // Check if metric belongs to this quarter/year
                            // If metric.quarter/year are undefined, assume it's for all quarters (or check your business logic)
                            // Usually strict EOS implies metrics are defined per quarter.
                            const mQ = m.quarter ? parseInt(m.quarter.replace('Q', '')) : q;
                            const mY = m.year || y;
                            return mQ === q && mY === y;
                          })}
                          metricGoals={metricGoals}
                          quarter={Math.floor(new Date().getMonth() / 3) + 1}
                          year={new Date().getFullYear()}
                          onUpdateGoal={async (metricId, periodNumber, value, notes, dateStart, dateEnd, goalId) => {
                             if (goalId) {
                                await updateMetricGoal(goalId, {
                                  actualValue: value === null ? undefined : value,
                                  notes,
                                });
                              } else {
                                await addMetricGoal({
                                  metricId,
                                  periodType: 'week',
                                  periodNumber,
                                  actualValue: value === null ? undefined : value,
                                  dateStart,
                                  dateEnd,
                                  notes,
                                });
                              }
                          }}
                          onDropToIssue={async (metric, week, actual, goal) => {
                            console.log('Drop to Issue clicked for metric:', metric.id);
                            try {
                              await createAndAddIssue({
                                name: `${metric.metricName || metric.measurable} Off Track`,
                                description: `Week ${week.weekNumber} (${week.label}): Actual ${actual} vs Goal ${goal}`,
                                priority: 'medium',
                                relatedMetricId: metric.id,
                                ownerId: metric.ownerId,
                                assignedToUserId: metric.ownerId,
                              });
                              console.log('Issue created successfully');
                              toast({
                                title: 'Issue Created',
                                description: 'Metric dropped to issues list',
                                status: 'success',
                                duration: 2000,
                              });
                            } catch (error) {
                              console.error('Error creating issue from metric:', error);
                              toast({
                                title: 'Error',
                                description: 'Failed to create issue',
                                status: 'error',
                                duration: 3000,
                              });
                            }
                          }}
                          users={users}
                        />

                        {isPresenter && (
                          <Box maxW="3xl" mx="auto">
                            <Box bg="gray.50" borderRadius="lg" p={4} borderWidth={1} borderColor={borderColor}>
                              <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
                                Add note for this section
                              </Text>
                              <Textarea
                                value={sectionNote}
                                onChange={(e) => setSectionNote(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                    handleAddSectionNote();
                                  }
                                }}
                                placeholder="Type your note here and press Cmd/Ctrl+Enter or click Add..."
                                size="sm"
                                rows={3}
                                mb={3}
                              />
                              <HStack spacing={4} mb={3}>
                                <Checkbox
                                  isChecked={sectionNoteCategory === 'general'}
                                  onChange={(e) => {
                                    setSectionNoteCategory(e.target.checked ? 'general' : 'department_communication');
                                    if (e.target.checked) {
                                      setSectionNoteOwners(new Set());
                                      setSectionNoteDepartments(new Set());
                                    }
                                  }}
                                  size="sm"
                                >
                                  <Text fontSize="sm">General Note</Text>
                                </Checkbox>
                                <Checkbox
                                  isChecked={sectionNoteCategory === 'department_communication'}
                                  onChange={(e) => {
                                    setSectionNoteCategory(e.target.checked ? 'department_communication' : 'general');
                                    if (!e.target.checked) {
                                      setSectionNoteOwners(new Set());
                                      setSectionNoteDepartments(new Set());
                                    }
                                  }}
                                  size="sm"
                                >
                                  <Text fontSize="sm">Department Communication</Text>
                                </Checkbox>
                              </HStack>
                              {sectionNoteCategory === 'department_communication' && (
                                <HStack spacing={4} align="start" mb={3} flexWrap="wrap">
                                  <Box flex={1} minW="200px">
                                    <Text fontSize="xs" fontWeight="semibold" mb={2} color="gray.700">
                                      Owner(s):
                                    </Text>
                                    <VStack spacing={1} align="stretch" maxH="120px" overflowY="auto" borderWidth={1} borderColor={borderColor} borderRadius="md" p={2} bg="white">
                                      {users.map((user) => (
                                        <Checkbox
                                          key={user.id}
                                          isChecked={sectionNoteOwners.has(user.id)}
                                          onChange={() => handleToggleSectionNoteOwner(user.id)}
                                          size="sm"
                                        >
                                          <Text fontSize="sm">{user.name}</Text>
                                        </Checkbox>
                                      ))}
                                    </VStack>
                                  </Box>
                                  <Box flex={1} minW="200px">
                                    <Text fontSize="xs" fontWeight="semibold" mb={2} color="gray.700">
                                      Department(s):
                                    </Text>
                                    <VStack spacing={1} align="stretch" maxH="120px" overflowY="auto" borderWidth={1} borderColor={borderColor} borderRadius="md" p={2} bg="white">
                                      {departments.map((dept) => (
                                        <Checkbox
                                          key={dept.id}
                                          isChecked={sectionNoteDepartments.has(dept.id)}
                                          onChange={() => handleToggleSectionNoteDepartment(dept.id)}
                                          size="sm"
                                        >
                                          <Text fontSize="sm">{dept.name}</Text>
                                        </Checkbox>
                                      ))}
                                    </VStack>
                                  </Box>
                                </HStack>
                              )}
                              <HStack justify="end" mt={2}>
                                <Button
                                  onClick={handleAddSectionNote}
                                  isDisabled={!sectionNote.trim() || (sectionNoteCategory === 'department_communication' && sectionNoteOwners.size === 0 && sectionNoteDepartments.size === 0)}
                                  size="sm"
                                  colorScheme="blue"
                                >
                                  Add to Notes
                                </Button>
                              </HStack>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}
                    {currentSection.key === 'customer_employee_headlines' && (
                      <Box py={6}>
                        <Text textAlign="center" fontSize="lg" color="gray.600" mb={6}>
                          Discuss customer and employee highlights
                        </Text>
                        {isPresenter && (
                          <Box maxW="3xl" mx="auto">
                            <Box bg="gray.50" borderRadius="lg" p={4} borderWidth={1} borderColor={borderColor}>
                              <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
                                Add note for this section
                              </Text>
                              <Textarea
                                value={sectionNote}
                                onChange={(e) => setSectionNote(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                    handleAddSectionNote();
                                  }
                                }}
                                placeholder="Type your note here and press Cmd/Ctrl+Enter or click Add..."
                                size="sm"
                                rows={3}
                                mb={3}
                              />
                              <HStack spacing={4} mb={3}>
                                <Checkbox
                                  isChecked={sectionNoteCategory === 'general'}
                                  onChange={(e) => {
                                    setSectionNoteCategory(e.target.checked ? 'general' : 'department_communication');
                                    if (e.target.checked) {
                                      setSectionNoteOwners(new Set());
                                      setSectionNoteDepartments(new Set());
                                    }
                                  }}
                                  size="sm"
                                >
                                  <Text fontSize="sm">General Note</Text>
                                </Checkbox>
                                <Checkbox
                                  isChecked={sectionNoteCategory === 'department_communication'}
                                  onChange={(e) => {
                                    setSectionNoteCategory(e.target.checked ? 'department_communication' : 'general');
                                    if (!e.target.checked) {
                                      setSectionNoteOwners(new Set());
                                      setSectionNoteDepartments(new Set());
                                    }
                                  }}
                                  size="sm"
                                >
                                  <Text fontSize="sm">Department Communication</Text>
                                </Checkbox>
                              </HStack>
                              {sectionNoteCategory === 'department_communication' && (
                                <HStack spacing={4} align="start" mb={3} flexWrap="wrap">
                                  <Box flex={1} minW="200px">
                                    <Text fontSize="xs" fontWeight="semibold" mb={2} color="gray.700">
                                      Owner(s):
                                    </Text>
                                    <VStack spacing={1} align="stretch" maxH="120px" overflowY="auto" borderWidth={1} borderColor={borderColor} borderRadius="md" p={2} bg="white">
                                      {users.map((user) => (
                                        <Checkbox
                                          key={user.id}
                                          isChecked={sectionNoteOwners.has(user.id)}
                                          onChange={() => handleToggleSectionNoteOwner(user.id)}
                                          size="sm"
                                        >
                                          <Text fontSize="sm">{user.name}</Text>
                                        </Checkbox>
                                      ))}
                                    </VStack>
                                  </Box>
                                  <Box flex={1} minW="200px">
                                    <Text fontSize="xs" fontWeight="semibold" mb={2} color="gray.700">
                                      Department(s):
                                    </Text>
                                    <VStack spacing={1} align="stretch" maxH="120px" overflowY="auto" borderWidth={1} borderColor={borderColor} borderRadius="md" p={2} bg="white">
                                      {departments.map((dept) => (
                                        <Checkbox
                                          key={dept.id}
                                          isChecked={sectionNoteDepartments.has(dept.id)}
                                          onChange={() => handleToggleSectionNoteDepartment(dept.id)}
                                          size="sm"
                                        >
                                          <Text fontSize="sm">{dept.name}</Text>
                                        </Checkbox>
                                      ))}
                                    </VStack>
                                  </Box>
                                </HStack>
                              )}
                              <HStack justify="end" mt={2}>
                                <Button
                                  onClick={handleAddSectionNote}
                                  isDisabled={!sectionNote.trim() || (sectionNoteCategory === 'department_communication' && sectionNoteOwners.size === 0 && sectionNoteDepartments.size === 0)}
                                  size="sm"
                                  colorScheme="blue"
                                >
                                  Add to Notes
                                </Button>
                              </HStack>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}
                    {currentSection.key === 'conclude' && activeMeeting && (
                      <Box py={6}>
                        {isPresenter && (
                          <Box maxW="3xl" mx="auto">
                            <Box bg="gray.50" borderRadius="lg" p={4} borderWidth={1} borderColor={borderColor}>
                              <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
                                Add note for this section
                              </Text>
                              <Textarea
                                value={sectionNote}
                                onChange={(e) => setSectionNote(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                    handleAddSectionNote();
                                  }
                                }}
                                placeholder="Type your note here and press Cmd/Ctrl+Enter or click Add..."
                                size="sm"
                                rows={3}
                                mb={3}
                              />
                              <HStack justify="end" mt={2}>
                                <Button
                                  onClick={handleAddSectionNote}
                                  isDisabled={!sectionNote.trim()}
                                  size="sm"
                                  colorScheme="blue"
                                >
                                  Add to Notes
                                </Button>
                              </HStack>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {lastStatusChange && (
            <Box
              position="fixed"
              bottom={4}
              right={4}
              bg="gray.800"
              color="white"
              px={4}
              py={3}
              borderRadius="lg"
              boxShadow="lg"
              zIndex={50}
            >
              <HStack spacing={3}>
                <Text fontSize="sm">Rock status updated</Text>
                <Button
                  size="xs"
                  colorScheme="blue"
                  variant="link"
                  onClick={handleUndoStatusChange}
                >
                  Undo
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => setLastStatusChange(null)}
                >
                  ×
                </Button>
              </HStack>
            </Box>
          )}

          {!meetingStarted && (
            <VStack spacing={3} mt={6}>
              {sections.map((section) => (
                <Box
                  key={section.key}
                  bg={bgColor}
                  borderWidth={1}
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                  w="full"
                >
                  <HStack justify="space-between">
                    <Text fontSize="base" fontWeight="semibold">
                      {section.label}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {section.duration} min
                    </Text>
                  </HStack>
                </Box>
              ))}
            </VStack>
          )}
        </GridItem>

        <GridItem>
          <Box
            bg={bgColor}
            borderWidth={1}
            borderColor={borderColor}
            borderRadius="lg"
            p={6}
            position="sticky"
            top="96px"
          >
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between" mb={4}>
                <Heading size="xl">Notes</Heading>
              </HStack>
              <VStack spacing={4} align="stretch">
                <Box
                  borderWidth={2}
                  borderColor="blue.200"
                  borderRadius="lg"
                  bg="blue.50"
                  p={3}
                >
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="bold" color="blue.900" textTransform="uppercase" letterSpacing="wide">
                      Presenter Notes
                    </Text>
                    {isPresenter && (
                      <Text fontSize="xs" color="blue.600" fontWeight="medium">
                        (You can edit)
                      </Text>
                    )}
                  </HStack>
                  <Textarea
                    ref={presenterNotesRef}
                    value={presenterNotes}
                    onChange={(e) => {
                      setPresenterNotes(e.target.value);
                      setSectionNotes({ ...sectionNotes, presenter: e.target.value });
                      setMeetingSaved(false);
                      setHasUnsavedChanges(true);
                    }}
                    placeholder={
                      isPresenter
                        ? 'Add presenter notes here...'
                        : 'No presenter notes yet...'
                    }
                    size="sm"
                    rows={4}
                    minH="100px"
                    isDisabled={!isPresenter || !meetingStarted}
                    bg="white"
                  />
                  <Text fontSize="xs" color="blue.700" mt={1}>
                    {isPresenter
                      ? 'Use this space for key points, reminders, prep notes, or follow-ups. Visible to all attendees.'
                      : "Presenter's notes for this meeting. These notes are visible to all attendees."}
                  </Text>
                </Box>
                <VStack spacing={4} align="stretch">
                  <SectionNotesEditor
                    sectionKey="segue"
                    sectionLabel="SEGUE NOTES"
                    sectionColor="green"
                    hintText="(Good news from previous week)"
                    description="Use this space to share good news from the previous week (5 minutes)."
                    notes={structuredNotes.individualNotes.segue || []}
                    users={users}
                    departments={departments}
                    onAddNote={async (note, category, owners, depts) => {
                      if (!meetingTimestamp) return;
                      await addIndividualNote('segue', note, category, meetingDate, meetingTimestamp, owners, depts);
                        setMeetingSaved(false);
                        setHasUnsavedChanges(true);
                    }}
                    onDeleteNote={async (noteId) => {
                      if (!meetingTimestamp) return;
                      await deleteIndividualNote(noteId, meetingTimestamp);
                      setHasUnsavedChanges(true);
                    }}
                    isPresenter={isPresenter}
                    isDisabled={!meetingStarted}
                  />
                  <SectionNotesEditor
                    sectionKey="scorecard"
                    sectionLabel="SCORECARD NOTES"
                    sectionColor="purple"
                    hintText="(Metrics review & discussion)"
                    description="Use this space for notes about scorecard metrics and their performance (5 minutes)."
                    notes={structuredNotes.individualNotes.scorecard || []}
                    users={users}
                    departments={departments}
                    onAddNote={async (note, category, owners, depts) => {
                      if (!meetingTimestamp) return;
                      await addIndividualNote('scorecard', note, category, meetingDate, meetingTimestamp, owners, depts);
                      setMeetingSaved(false);
                      setHasUnsavedChanges(true);
                    }}
                    onDeleteNote={async (noteId) => {
                      if (!meetingTimestamp) return;
                      await deleteIndividualNote(noteId, meetingTimestamp);
                      setHasUnsavedChanges(true);
                    }}
                    isPresenter={isPresenter}
                    isDisabled={!meetingStarted}
                  />
                  <SectionNotesEditor
                    sectionKey="customer_employee_headlines"
                    sectionLabel="CUSTOMER/EMPLOYEE NOTES"
                    sectionColor="orange"
                    hintText="(Highlights & feedback)"
                    description="Use this space for notes about customer feedback and employee highlights (5 minutes)."
                    notes={structuredNotes.individualNotes.customer_employee_headlines || []}
                    users={users}
                    departments={departments}
                    onAddNote={async (note, category, owners, depts) => {
                      if (!meetingTimestamp) return;
                      await addIndividualNote('customer_employee_headlines', note, category, meetingDate, meetingTimestamp, owners, depts);
                      setMeetingSaved(false);
                      setHasUnsavedChanges(true);
                    }}
                    onDeleteNote={async (noteId) => {
                      if (!meetingTimestamp) return;
                      await deleteIndividualNote(noteId, meetingTimestamp);
                      setHasUnsavedChanges(true);
                    }}
                    isPresenter={isPresenter}
                    isDisabled={!meetingStarted}
                  />
                  <SectionNotesEditor
                    sectionKey="conclude"
                    sectionLabel="CONCLUSION NOTES"
                    sectionColor="teal"
                    hintText="(Next steps & recap)"
                    description="Use this space for meeting wrap-up, cascading messages, and next steps (5 minutes)."
                    notes={structuredNotes.individualNotes.conclude || []}
                    users={users}
                    departments={departments}
                    onAddNote={async (note, category, owners, depts) => {
                      if (!meetingTimestamp) return;
                      await addIndividualNote('conclude', note, category, meetingDate, meetingTimestamp, owners, depts);
                      setMeetingSaved(false);
                      setHasUnsavedChanges(true);
                    }}
                    onDeleteNote={async (noteId) => {
                      if (!meetingTimestamp) return;
                      await deleteIndividualNote(noteId, meetingTimestamp);
                      setHasUnsavedChanges(true);
                    }}
                    isPresenter={isPresenter}
                    isDisabled={!meetingStarted}
                  />
                    </VStack>
              </VStack>
            </VStack>
          </Box>
        </GridItem>
      </Grid>

      <OffTrackMetricsModal
        isOpen={showOffTrackModal}
        onClose={() => setShowOffTrackModal(false)}
        metrics={offTrackMetrics}
        onAddMetrics={handleAddOffTrackMetrics}
        onSkip={handleSkipOffTrackMetrics}
      />


      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteTimestamp(null);
        }}
        onConfirm={confirmDeleteMeeting}
        title="Delete Meeting?"
        message="Are you sure you want to delete this meeting? This action cannot be undone."
        icon="warning"
      />

      <OutstandingIssuesModal
        isOpen={showOutstandingIssuesModal}
        onClose={() => setShowOutstandingIssuesModal(false)}
        issueCount={outstandingIssuesCount}
      />

      <AddIssueModal
        isOpen={showAddIssueModal}
        onClose={() => {
          setShowAddIssueModal(false);
          setRockForIssueRequired(null);
          setPendingRockStatusChange(null);
          setAddIssueModalButtonText('Save Issue');
          setAddIssueModalContext(undefined);
        }}
        onSave={async (formData: any) => {
          if (!rockForIssueRequired) return;
          
          // Create the issue with the rockId (use formData.relatedRockId if provided, otherwise use rockForIssueRequired.id)
          const relatedRockId = formData.relatedRockId || rockForIssueRequired.id;
          
          await createAndAddIssue({
            name: formData.name,
            description: formData.description,
            priority: 'medium', // Default priority since field was removed from modal
            relatedRockId: relatedRockId,
            isRock: true,
          });

          // Trigger refresh for the rock that got the new issue
          setRockRefreshTrigger((prev) => ({
            ...prev,
            [relatedRockId]: (prev[relatedRockId] || 0) + 1,
          }));

          // Issue creation automatically makes rock off-track (computed from issues)
          const wasFromOffTrackClick = pendingRockStatusChange?.newStatus === 'off-track';
          
          toast({
            title: 'Success',
            description: wasFromOffTrackClick 
              ? 'Issue created. Rock is now off-track.' 
              : 'Issue created successfully.',
            status: 'success',
            duration: 3000,
          });

          setShowAddIssueModal(false);
          setRockForIssueRequired(null);
          setPendingRockStatusChange(null);
          setAddIssueModalContext(undefined);
        }}
        currentUserId={currentUser?.uid}
        initialRelatedRockId={rockForIssueRequired?.id}
        submitButtonText={addIssueModalButtonText}
        context={addIssueModalContext}
      />

      {resolvingIssue && (
        <ResolveIssueModal
          isOpen={resolveModalOpen}
          onClose={() => {
            setResolveModalOpen(false);
            setResolvingIssue(null);
          }}
          onConfirm={handleConfirmResolve}
          issueTitle={resolvingIssue.issueName}
          isLastIssue={resolvingIssue.isLastIssue}
          onMarkOnTrack={handleMarkOnTrack}
        />
      )}

      <TangentModal
        isOpen={showTangentModal}
        triggeredByUser={users.find((u) => u.id === tangentTriggeredByUserId)}
        onClose={() => {
          setShowTangentModal(false);
          // Mark the latest alert as dismissed for this user
          if (activeMeeting?.id) {
            const tangentAlertsRef = collection(db, COLLECTION_NAMES.TANGENT_ALERTS);
            const q = query(
              tangentAlertsRef,
              where('meetingId', '==', activeMeeting.id),
              orderBy('createdAt', 'desc'),
              limit(1)
            );
            getDocs(q).then((snapshot) => {
              if (!snapshot.empty) {
                const latestAlertId = snapshot.docs[0].id;
                setDismissedTangentAlerts((prev) => {
                  const updated = new Set(prev).add(latestAlertId);
                  // Persist to sessionStorage
                  sessionStorage.setItem('dismissed_tangent_alerts', JSON.stringify(Array.from(updated)));
                  return updated;
                });
              }
            });
          }
        }}
      />

      <EditRockCountersModal
        isOpen={showEditCountersModal}
        onClose={() => {
          setShowEditCountersModal(false);
          setEditingRockId(null);
        }}
        onSave={handleSaveCounters}
        rockName={editingRockName}
        currentConsecutive={editingConsecutive}
        currentTotal={editingTotal}
      />

      <Modal
        isOpen={showAddActionItemNoteModal}
        onClose={() => {
          setShowAddActionItemNoteModal(false);
          setActionItemForNote(null);
          setActionItemNoteText('');
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Action Item Note to Meeting Notes</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Box w="full">
                <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
                  Enter your notes about this action item:
                </Text>
                <Textarea
                  value={actionItemNoteText}
                  onChange={(e) => setActionItemNoteText(e.target.value)}
                  placeholder="Type your notes here..."
                  minH="120px"
                  autoFocus
                />
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => {
                setShowAddActionItemNoteModal(false);
                setActionItemForNote(null);
                setActionItemNoteText('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveActionItemNote}
              isDisabled={!actionItemNoteText.trim()}
              colorScheme="blue"
            >
              Add to Notes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal
        isOpen={showStopMeetingModal}
        onClose={() => setShowStopMeetingModal(false)}
        onConfirm={executeStopMeeting}
        title="Stop Meeting Without Notes?"
        message="You have not entered any notes for this meeting. The meeting will be saved with a placeholder note &quot;(No notes recorded)&quot; and then completed. Continue?"
        confirmText="Yes, Stop Meeting"
        cancelText="Cancel, Keep Editing"
        icon="warning"
      />
    </Container>
  );
}

